import deepEqual from "fast-deep-equal"

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (value === null || typeof value !== "object") {
    return false
  }
  const proto = Object.getPrototypeOf(value)
  return proto === Object.prototype || proto === null
}

const isEmptyValue = (value: unknown): boolean => {
  if (value == null) {
    return true
  }
  if (Array.isArray(value)) {
    return value.length === 0 || value.every(item => isEmptyValue(item))
  }
  if (isPlainObject(value)) {
    const keys = Object.keys(value)
    return keys.length === 0 || keys.every(key => isEmptyValue(value[key]))
  }
  return false
}

type MemoizeOneOptions = {
  cachePromiseRejection?: boolean
  ttl?: number
  isEqual?: typeof deepEqual
}

export const memoizeOne = <
  TResult,
  // biome-ignore lint/suspicious/noExplicitAny: TODO FIX
  TFunc extends (this: unknown, ...newArgs: any[]) => TResult,
>(
  fn: TFunc,
  {
    isEqual = deepEqual,
    cachePromiseRejection = false,
    ttl = 0,
  }: MemoizeOneOptions = {},
): TFunc => {
  let calledOnce = false
  let oldArgs: unknown[] | undefined
  let lastResult: unknown

  return ((...newArgs) => {
    if (calledOnce && isEqual(newArgs, oldArgs)) {
      return lastResult
    }

    lastResult = fn(...newArgs)

    // Decide whether the result should be cached
    const shouldCache = !isEmptyValue(lastResult)

    if (!shouldCache) {
      // Do not cache empty values; ensure next invocation recomputes
      calledOnce = false
      return lastResult
    }

    const ttlTimeout = ttl
      ? setTimeout(() => {
          calledOnce = false
        }, ttl)
      : undefined

    if (lastResult instanceof Promise) {
      lastResult
        .then(resolved => {
          if (isEmptyValue(resolved)) {
            calledOnce = false
            clearTimeout(ttlTimeout as number | undefined)
          }
        })
        .catch(() => {
          if (!cachePromiseRejection) {
            calledOnce = false
            clearTimeout(ttlTimeout as number | undefined)
          }
        })
    }

    calledOnce = true
    oldArgs = newArgs

    return lastResult
  }) as TFunc
}
